import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { globalStore } from '../../GlobalStore';
import { readAppOpenBaseLink } from '../../utils/AppDownloadUrlConfig';
import {
  buildAppOpenUrl,
  detectMobileOs,
  isWechatBrowser,
  openAppWithDownloadFallback,
  parseHelloAppH5Source,
  resolveNativeDownloadUrl,
} from '../../utils/AppOpenLinkUtil';
import { LoginModalConfig } from '../../components/LoginModal/LoginModalConfig';
import './HelloAppH5Style.css';

export type HelloAppH5Role = 'buyer' | 'seller';

export interface HelloAppH5PageProps {
  role: HelloAppH5Role;
  documentTitle: string;
  buttonLabel?: string;
  shareButtonLabel?: string;
  downloadButtonLabel?: string;
  iosAppStoreUrl?: string;
  androidApkUrl?: string;
  /** 分享场景仅用 Universal Link / App Link 基址，不带 H5 路由路径 */
  shareOpenBaseOnly?: boolean;
  backgroundImageUrl: string;
  placeholderBackgroundColor: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
}

const HelloAppH5Page: React.FC<HelloAppH5PageProps> = observer(
  ({
    role,
    documentTitle,
    buttonLabel,
    shareButtonLabel,
    downloadButtonLabel,
    iosAppStoreUrl = '',
    androidApkUrl = '',
    shareOpenBaseOnly = false,
    backgroundImageUrl,
    placeholderBackgroundColor,
    buttonBackgroundColor = LoginModalConfig.primaryColor,
    buttonTextColor = '#ffffff',
  }) => {
    const location = useLocation();
    const source = parseHelloAppH5Source(location.search);
    const os = detectMobileOs();
    const inWechat = useMemo(() => isWechatBrowser(), []);
    const hasDownloadTargets = Boolean(iosAppStoreUrl.trim() || androidApkUrl.trim());
    const isDownloadMode = source === 'download' && hasDownloadTargets;

    useEffect(() => {
      window.scrollTo(0, 0);
      document.title = documentTitle;
    }, [documentTitle]);

    const resolveAppOpenUrl = useCallback((): string => {
      const baseLink = readAppOpenBaseLink(globalStore.basicConfigs, role, os);
      if (shareOpenBaseOnly) {
        return baseLink.trim();
      }
      return buildAppOpenUrl(baseLink, location.pathname, location.search);
    }, [location.pathname, location.search, os, role, shareOpenBaseOnly]);

    const resolveDownloadUrl = useCallback(
      (): string => resolveNativeDownloadUrl(os, iosAppStoreUrl, androidApkUrl),
      [androidApkUrl, iosAppStoreUrl, os]
    );

    const actionLabel = isDownloadMode
      ? downloadButtonLabel || '下载APP'
      : shareButtonLabel || buttonLabel || '打开';

    const handlePrimaryAction = () => {
      if (inWechat) {
        return;
      }
      if (isDownloadMode) {
        const url = resolveDownloadUrl();
        if (!url) {
          console.warn('[HelloAppH5] 未配置下载链接');
          return;
        }
        window.location.href = url;
        return;
      }
      const appUrl = resolveAppOpenUrl();
      const downloadUrl = resolveDownloadUrl();
      if (!appUrl && !downloadUrl) {
        console.warn('[HelloAppH5] 未配置唤起或下载链接');
        return;
      }
      openAppWithDownloadFallback(appUrl, downloadUrl);
    };

    const canAct = isDownloadMode ? Boolean(resolveDownloadUrl()) : Boolean(resolveAppOpenUrl() || resolveDownloadUrl());

    return (
      <div className="hello-app-h5-page">
        <div
          className="hello-app-h5-bg"
          style={{
            backgroundColor: placeholderBackgroundColor,
            backgroundImage: `url(${backgroundImageUrl})`,
          }}
          aria-hidden
        />
        {inWechat ? (
          <div className="hello-app-h5-wechat-mask" role="dialog" aria-label="请在浏览器中打开">
            <div className="hello-app-h5-wechat-arrow" aria-hidden />
            <p className="hello-app-h5-wechat-title">无法在微信内直接打开</p>
            <p className="hello-app-h5-wechat-desc">请点击右上角 ···，选择「在浏览器中打开」</p>
          </div>
        ) : null}
        <div className="hello-app-h5-btn-wrap">
          <button
            type="button"
            className="hello-app-h5-open-btn"
            style={{
              backgroundColor: buttonBackgroundColor,
              color: buttonTextColor,
              opacity: 1,
            }}
            aria-disabled={!canAct || inWechat}
            onClick={handlePrimaryAction}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    );
  }
);

export default HelloAppH5Page;
